import { Box, Typography } from '@mui/material';
import PlusIcon from 'assets/add-white.svg';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomNavigationTab from 'components/shared/CustomNavigationTab/CustomNavigationTab';
import { default as CustomTab } from 'components/shared/CustomTab/CustomTab';
import { LayoutSwitcher } from 'components/shared/LayoutSwitcher';
import SearchInput from 'components/shared/SearchInput';
import { useInputWithDebounce } from 'customHook/useInputWithDebounce';
import { LayoutType } from 'model/deals.model';
import { SyntheticEvent, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  setAdminSearchModal,
  setCategoryValue,
  setPage,
  setSearchTerm,
  setTabValue,
} from 'store/slices/businessesSlice';
import { styles } from './styles';
import { usePermissions } from 'customHook/usePermissions';

export const Filters = ({
  layout,
  onLayoutChange,
  tabs,
}: {
  layout: LayoutType;
  onLayoutChange: (_: SyntheticEvent, newValue: LayoutType) => void;
  tabs: { label: string; id: number; isHidden: boolean; Component: JSX.Element }[];
}) => {
  const dispatch = useAppDispatch();
  const { categories, categoryValue, searchTerm, tabValue } = useAppSelector(
    (state) => state.businesses,
  );
  const { hasAction, hasFullAccess } = usePermissions();
  const canCreateMyBusiness =
    hasAction('businesses', 'mybusinesses', 'create') || hasFullAccess('businesses');
  const canCreateAllBusiness =
    hasAction('businesses', 'allbusinesses', 'create') || hasFullAccess('businesses');

  const { value, debouncedValue, handleChange, clearInput } = useInputWithDebounce(searchTerm, 500);

  useEffect(() => {
    dispatch(setSearchTerm(debouncedValue));
  }, [debouncedValue, dispatch]);

  const isMybusinessesTab = tabs[tabValue]?.label?.includes('My Businesses');
  const categoryTabs = categories.map((category, index) => {
    const { name, count } = category;
    return {
      label: `${name} (${count})`,
      id: index + 1,
      isHidden: tabValue !== index,
      Component: <></>,
    };
  });

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(event);
    dispatch(setPage(1));
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    dispatch(setTabValue(newValue));
  };

  const handleCategoryNavigationTabChange = (_: React.SyntheticEvent, newValue: number) => {
    dispatch(setCategoryValue(newValue));
  };

  const handleNewBusinessClick = () => {
    dispatch(setAdminSearchModal(true));
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <Box>
          <CustomTab tabs={tabs} handleChange={handleTabChange} value={tabValue} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            gap: { xs: '8px', sm: '12px' },
            alignItems: 'center',
          }}
        >
          <Box sx={{ width: { xl: '240px', xs: '150px' } }}>
            <SearchInput value={value} handleChange={handleSearch} clearInput={clearInput} />
          </Box>

          <LayoutSwitcher layout={layout} onLayoutChange={onLayoutChange} />

          {(isMybusinessesTab && canCreateMyBusiness) ||
          (!isMybusinessesTab && canCreateAllBusiness) ? (
            <CustomButton
              icon={<img src={PlusIcon} alt='white arrow' />}
              iconPosition='start'
              variant={'contained'}
              text={<Typography variant='responsiveButtonMedium'>New Business</Typography>}
              style={{ ...styles.button }}
              onClick={handleNewBusinessClick}
              disabled={false}
            />
          ) : null}
        </Box>
      </Box>
      <Box>
        <CustomNavigationTab
          tabs={categoryTabs}
          handleChange={handleCategoryNavigationTabChange}
          value={categoryValue}
        />
      </Box>
    </Box>
  );
};
